import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { FieldPath, useForm, UseFormGetValues } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  PatientHeaderLabel,
  PatientHeaderTitle,
} from "../../../constants/Patient";
import { PatientHeaderResponse } from "../../../models/Api/AllPatientRecord";
import { PatientHeaderFormValues } from "../../../models/Patient";
import { a11yProps, decryptId, isEmptyValue } from "../../../utils";
import {
  BCBSMemberStyle,
  PanelBox,
  PatientHeaderTab,
  PatientHeaderTabs,
  TabsPanelSectionBox,
} from "../style";
import AddressInfo from "./AddressInfo";
import MemberInfo from "./MemberInfo";
import PcpInfo from "./PcpInfo";
import PrimarySubscriberInfo from "./PrimarySubScriberInfo";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
export interface PatientHeaderFormProps {
  getValues: UseFormGetValues<PatientHeaderFormValues>;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <TabsPanelSectionBox
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <PanelBox sx={BCBSMemberStyle()}>
          <Box>{children}</Box>
        </PanelBox>
      )}
    </TabsPanelSectionBox>
  );
}

export const fieldNames: FieldPath<PatientHeaderFormValues>[] = [
  "primaryPhysician.physicianName",
  "primaryPhysician.physicianNpiId",
  "primaryPhysician.phoneNo",
  "primaryPhysician.streetName1",
  "primaryPhysician.county",
  "primaryPhysician.zipCode",
  "primaryPhysician.taxId",
  "primaryPhysician.fax",
  "primaryPhysician.streetName2",
  "primaryPhysician.physicianState",
  "primaryPhysician.city",
];

type KeyValueObject = { [key: string]: string | number | null };

export const getFormValues = (
  fields: string[],
  getValues: (field: string) => string | number | null
): KeyValueObject => {
  return fields.reduce((acc, field) => {
    acc[field] = getValues(field);
    return acc;
  }, {} as KeyValueObject);
};
export interface PropsFromState {
  patientHeaderData: PatientHeaderResponse;
}
export interface PropsFromDispatch {
  getPatientHeader: (patientId: number, refrralId: number) => void;
}
type Props = PropsFromState & PropsFromDispatch;

const PatientHeader: React.FC<Props> = ({
  patientHeaderData,
  getPatientHeader,
}: Props) => {
  const { response } = patientHeaderData;
  const [activeTab, setActiveTab] = useState(0);
  const [referralId, setReferralId] = useState<number | null>(null);
  const [decryptedPatientId, setDecryptedPatientId] = useState<number | null>(
    null
  );

  const methods = useForm<PatientHeaderFormValues>({
    defaultValues: {},
    mode: "all",
  });
  const { getValues, trigger, reset } = methods;
  const { patientId, authNo } = useParams();

  useEffect(() => {
    const loadIds = async () => {
      if (authNo) {
        const decryptedAuthNo = await decryptId(authNo);
        const parsedAuthNo = Number(decryptedAuthNo);
        setReferralId(Number.isFinite(parsedAuthNo) ? parsedAuthNo : null);
      } else {
        setReferralId(null);
      }

      if (patientId) {
        const decryptedPid = await decryptId(patientId);
        const parsedPatientId = Number(decryptedPid);
        setDecryptedPatientId(
          Number.isFinite(parsedPatientId) ? parsedPatientId : null
        );
      } else {
        setDecryptedPatientId(null);
      }
    };

     void loadIds().catch(() => {
       setReferralId(null);
       setDecryptedPatientId(null);
     });
  }, [authNo, patientId]);

  useEffect(() => {
    if (decryptedPatientId || referralId) {
      const payload = {
        referralId: referralId ?? 0,
        patientId: decryptedPatientId ?? 0,
      };
      getPatientHeader(payload.patientId, payload.referralId);
    }
  }, [decryptedPatientId, referralId, getPatientHeader]);

  useEffect(() => {
    if (response && response.patientId > 0) {
      reset(response);
    }
  }, [response]);

  const handleChange = async (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    const error = await trigger();
    if (isEmptyValue(getFormValues(fieldNames, getValues)) && activeTab == 2) {
      setActiveTab(newValue);
    } else if (error) {
      setActiveTab(newValue);
    }
  };
  const getPatientHeaderLabels = (relationshipType?: string) => {
    const isOtherRelationship =
      relationshipType?.trim().toUpperCase() === "OTHER";
  
    return PatientHeaderLabel.filter(
      ({ label }) =>
        label !== PatientHeaderTitle.PRIMARY_SUBSCRIBER_INFORMATION ||
        isOtherRelationship
    );
  };
  const headers = getPatientHeaderLabels(
    getValues("memberInfo.relationshipType")
      ? getValues("memberInfo.relationshipType")
      : ""
  );
  return (
    <Box>
      <Box>
        <PatientHeaderTabs
          value={activeTab}
          onChange={handleChange}
          data-testid={"patient-header-tabs"}
          aria-label="Patient Header Tabs"
        >
          {headers.map((data, index) => (
            <PatientHeaderTab
              key={data.label}
              label={data.label}
              {...a11yProps(index)}
              data-testid={`tab-${data.label}`}
            />
          ))}
        </PatientHeaderTabs>
      </Box>
      <TabPanel value={activeTab} index={0}>
        <MemberInfo getValues={getValues} />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <AddressInfo getValues={getValues} />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <PcpInfo getValues={getValues} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <PrimarySubscriberInfo getValues={getValues} />
      </TabPanel>
    </Box>
  );
};

export default PatientHeader;
